import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import {
  MessageSquare,
  Bot,
  Shield,
  FileSearch,
  FileCheck,
  BookOpen,
  Zap,
  Calendar,
  FileStack,
  BarChart3,
  Search,
  Database,
  CreditCard,
  Settings,
  Bookmark,
  Receipt,
  Sparkles,
  ArrowLeft,
  HelpCircle,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';

const sections = [
  {
    id: 'chat',
    icon: MessageSquare,
    title: 'Chat z KAI',
    color: 'text-orange-500',
    bg: 'bg-orange-500/10',
    steps: [
      { label: 'Nowa rozmowa', desc: 'Kliknij "Nowa Rozmowa" w panelu bocznym, aby rozpocząć czat z asystentem KAI.' },
      { label: 'Zadaj pytanie', desc: 'Wpisz pytanie w pole tekstowe na dole ekranu i naciśnij Enter lub kliknij ikonę wysyłania.' },
      { label: 'Wybór modelu AI', desc: 'W prawym górnym rogu możesz wybrać dostawcę AI (OpenAI, Anthropic, Gemini) oraz konkretny model.' },
      { label: 'Załączniki', desc: 'Kliknij ikonę spinacza, aby dołączyć plik (PDF, obraz, dokument) do pytania. KAI przeanalizuje jego treść.' },
      { label: 'Rozmowa głosowa', desc: 'Kliknij ikonę mikrofonu, aby dyktować pytanie głosowo. KAI zamieni mowę na tekst.' },
      { label: 'Źródła i cytaty', desc: 'Pod odpowiedzią KAI wyświetlają się źródła z bazy wiedzy i portali prawnych, na których oparta jest odpowiedź.' },
    ],
  },
  {
    id: 'history',
    icon: Bookmark,
    title: 'Historia i Ulubione',
    color: 'text-yellow-500',
    bg: 'bg-yellow-500/10',
    steps: [
      { label: 'Historia rozmów', desc: 'Wszystkie wcześniejsze rozmowy widoczne są w panelu bocznym. Kliknij na wybraną rozmowę, aby ją otworzyć.' },
      { label: 'Usuwanie rozmowy', desc: 'Najedź na rozmowę w panelu bocznym i kliknij ikonę kosza, aby usunąć rozmowę.' },
      { label: 'Eksport do PDF', desc: 'Otwórz rozmowę, a następnie kliknij ikonę pobierania, aby wyeksportować ją do pliku PDF.' },
      { label: 'Udostępnianie', desc: 'Kliknij ikonę udostępniania, aby wygenerować link do rozmowy, który możesz przesłać innym.' },
      { label: 'Ulubione odpowiedzi', desc: 'Przejdź do sekcji "Ulubione" w menu bocznym, aby zobaczyć zapisane odpowiedzi.' },
    ],
  },
  {
    id: 'review',
    icon: FileSearch,
    title: 'Sprawdź dokument',
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    steps: [
      { label: 'Prześlij dokument', desc: 'Przejdź do "Sprawdź dokument" i załaduj plik PDF, DOCX lub zdjęcie dokumentu.' },
      { label: 'Analiza AI', desc: 'KAI automatycznie przeanalizuje treść dokumentu i wygeneruje raport z uwagami.' },
      { label: 'Zadaj pytania', desc: 'Po analizie możesz zadawać dodatkowe pytania dotyczące treści dokumentu.' },
    ],
  },
  {
    id: 'invoice-auditor',
    icon: FileCheck,
    title: 'Audytor faktur',
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    steps: [
      { label: 'Wprowadź dane', desc: 'Wklej dane faktury lub prześlij jej zdjęcie/skan do analizy.' },
      { label: 'Walidacja', desc: 'KAI sprawdzi poprawność danych, w tym NIP, stawki VAT, daty i wymagane elementy.' },
      { label: 'Raport błędów', desc: 'Otrzymasz szczegółowy raport z listą wykrytych nieprawidłowości i sugestiami poprawek.' },
    ],
  },
  {
    id: 'translator',
    icon: BookOpen,
    title: 'Tłumacz przepisów',
    color: 'text-violet-500',
    bg: 'bg-violet-500/10',
    steps: [
      { label: 'Wklej przepis', desc: 'Skopiuj fragment przepisu prawnego i wklej go w pole tekstowe.' },
      { label: 'Wybierz odbiorcę', desc: 'Określ, dla kogo ma być tłumaczenie: laik, przedsiębiorca czy księgowy.' },
      { label: 'Otrzymaj wyjaśnienie', desc: 'KAI przetłumaczy skomplikowany tekst prawny na prosty, zrozumiały język z przykładami.' },
    ],
  },
  {
    id: 'specialist-bots',
    icon: Bot,
    title: 'Specjaliści AI',
    color: 'text-cyan-500',
    bg: 'bg-cyan-500/10',
    steps: [
      { label: 'Wybierz specjalistę', desc: 'Dostępni są specjaliści od ZUS, VAT, PIT/CIT, prawa pracy i rachunkowości.' },
      { label: 'Zadaj pytanie', desc: 'Każdy specjalista ma wiedzę z dedykowanego obszaru i udziela odpowiedzi z powołaniem na podstawy prawne.' },
      { label: 'Weryfikacja odpowiedzi', desc: 'Możesz poprosić specjalistę o weryfikację odpowiedzi z innej rozmowy.' },
    ],
  },
  {
    id: 'templates',
    icon: FileStack,
    title: 'Szablony pism',
    color: 'text-indigo-500',
    bg: 'bg-indigo-500/10',
    steps: [
      { label: 'Wybierz szablon', desc: 'Przeglądaj dostępne szablony dokumentów księgowych i prawnych.' },
      { label: 'Wypełnij dane', desc: 'Uzupełnij formularz wymaganymi danymi (daty, kwoty, dane firmy).' },
      { label: 'Generuj PDF', desc: 'Kliknij "Generuj dokument", aby pobrać gotowy plik PDF.' },
    ],
  },
  {
    id: 'tax-comparison',
    icon: BarChart3,
    title: 'Porównywarka podatków',
    color: 'text-pink-500',
    bg: 'bg-pink-500/10',
    steps: [
      { label: 'Wprowadź dane', desc: 'Podaj przychody, koszty i inne parametry Twojej działalności.' },
      { label: 'Porównaj formy', desc: 'System porówna opodatkowanie na zasadach ogólnych, liniowe, ryczałt i kartę podatkową.' },
      { label: 'Analiza wyników', desc: 'Otrzymasz tabelę z porównaniem zobowiązań podatkowych i rekomendację najkorzystniejszej formy.' },
    ],
  },
  {
    id: 'jpk',
    icon: Search,
    title: 'Analiza JPK',
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
    steps: [
      { label: 'Załaduj plik JPK', desc: 'Prześlij plik JPK_VAT w formacie XML do analizy.' },
      { label: 'Automatyczna weryfikacja', desc: 'System sprawdzi strukturę pliku, poprawność danych i spójność kwot.' },
      { label: 'Raport i pytania', desc: 'Po analizie możesz przeglądać raport i zadawać pytania o szczegóły pliku.' },
    ],
  },
  {
    id: 'ksef',
    icon: Zap,
    title: 'KSeF 2.0',
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    steps: [
      { label: 'Konfiguracja', desc: 'Połącz się z KSeF podając token autoryzacyjny lub certyfikat kwalifikowany.' },
      { label: 'Generuj faktury', desc: 'Utwórz fakturę zgodną ze schematem FA(2), uzupełniając dane sprzedawcy i nabywcy.' },
      { label: 'Walidacja XML', desc: 'Przed wysyłką KAI zwaliduje plik XML pod kątem zgodności ze schematem KSeF.' },
      { label: 'Wysyłka do KSeF', desc: 'Wyślij fakturę do produkcyjnego API Ministerstwa Finansów i otrzymaj numer referencyjny.' },
    ],
  },
  {
    id: 'import-data',
    icon: Database,
    title: 'Import danych',
    color: 'text-teal-500',
    bg: 'bg-teal-500/10',
    steps: [
      { label: 'Wybierz plik', desc: 'Załaduj plik CSV lub XML z danymi księgowymi z programu księgowego.' },
      { label: 'Automatyczna analiza', desc: 'KAI rozpozna strukturę danych i przeprowadzi wstępną analizę.' },
      { label: 'Pytania o dane', desc: 'Zadawaj pytania o załadowane dane — np. o sumy, anomalie czy brakujące wpisy.' },
    ],
  },
  {
    id: 'calendar',
    icon: Calendar,
    title: 'Kalendarz podatkowy',
    color: 'text-sky-500',
    bg: 'bg-sky-500/10',
    steps: [
      { label: 'Przegląd terminów', desc: 'Kalendarz pokazuje wszystkie nadchodzące terminy podatkowe (PIT, CIT, VAT, ZUS).' },
      { label: 'Oznaczenia kolorami', desc: 'Pilne terminy (do 3 dni) oznaczone są na czerwono, bliskie (do 7 dni) na żółto.' },
      { label: 'Szczegóły terminu', desc: 'Kliknij na termin, aby zobaczyć szczegóły i powiązane przepisy.' },
    ],
  },
  {
    id: 'law-changes',
    icon: Shield,
    title: 'Zmiany w przepisach',
    color: 'text-orange-600',
    bg: 'bg-orange-600/10',
    steps: [
      { label: 'Przegląd zmian', desc: 'Strona wyświetla najnowsze zmiany w przepisach podatkowych i księgowych.' },
      { label: 'Automatyczna aktualizacja', desc: 'Baza zmian jest automatycznie aktualizowana 3 razy dziennie ze źródeł rządowych.' },
      { label: 'Szczegóły zmiany', desc: 'Kliknij na zmianę, aby zobaczyć pełny opis, datę wejścia w życie i podstawę prawną.' },
    ],
  },
  {
    id: 'subscription',
    icon: CreditCard,
    title: 'Subskrypcja i płatności',
    color: 'text-green-500',
    bg: 'bg-green-500/10',
    steps: [
      { label: 'Okres próbny', desc: 'Każde nowe konto otrzymuje 7 dni bezpłatnego dostępu do wszystkich funkcji.' },
      { label: 'Wybór planu', desc: 'Po okresie próbnym wybierz plan miesięczny (59 zł netto) lub roczny (601,80 zł netto, -15%).' },
      { label: 'Płatność', desc: 'Płatności obsługiwane są przez Tpay — dostępne BLIK, przelewy bankowe i karty.' },
      { label: 'Zarządzanie', desc: 'W sekcji "Subskrypcja" możesz sprawdzić status, zmienić plan lub pobrać fakturę.' },
    ],
  },
  {
    id: 'invoices',
    icon: Receipt,
    title: 'Faktury',
    color: 'text-slate-500',
    bg: 'bg-slate-500/10',
    steps: [
      { label: 'Tworzenie faktury', desc: 'Przejdź do "Faktury" i wypełnij dane sprzedawcy, nabywcy oraz pozycje faktury.' },
      { label: 'Automatyczne obliczenia', desc: 'System automatycznie obliczy kwoty netto, VAT i brutto.' },
      { label: 'Eksport i druk', desc: 'Wygeneruj fakturę jako PDF gotowy do druku lub wysyłki.' },
    ],
  },
  {
    id: 'calculator',
    icon: Sparkles,
    title: 'Kalkulator',
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
    steps: [
      { label: 'Wybierz kalkulator', desc: 'Dostępne są kalkulatory: wynagrodzeń, VAT, amortyzacji i inne.' },
      { label: 'Wprowadź dane', desc: 'Podaj wymagane wartości (kwoty, stawki, okresy).' },
      { label: 'Wyniki', desc: 'Otrzymasz szczegółowe obliczenie z rozbiciem na poszczególne składniki.' },
    ],
  },
  {
    id: 'settings',
    icon: Settings,
    title: 'Ustawienia konta',
    color: 'text-gray-500',
    bg: 'bg-gray-500/10',
    steps: [
      { label: 'Profil', desc: 'Zmień imię i nazwisko, avatar oraz adres email.' },
      { label: 'Hasło', desc: 'Zmień hasło do konta w sekcji bezpieczeństwa.' },
      { label: 'Preferencje', desc: 'Dostosuj tryb kolorystyczny (jasny/ciemny) i inne preferencje interfejsu.' },
    ],
  },
];

const pathMap = {
  review: '/review',
  'invoice-auditor': '/invoice-auditor',
  translator: '/regulation-translator',
  'specialist-bots': '/specialist-bots',
  templates: '/templates',
  'tax-comparison': '/tax-comparison',
  jpk: '/jpk',
  ksef: '/ksef',
  'import-data': '/import-data',
  calendar: '/calendar',
  'law-changes': '/law-changes',
  subscription: '/subscription',
  invoices: '/invoices',
  calculator: '/calculator',
  settings: '/settings',
};

function AccordionSection({ section, isOpen, onToggle, isDark, navigate }) {
  const Icon = section.icon;
  return (
    <div
      id={`section-${section.id}`}
      className={`rounded-xl border overflow-hidden transition-all duration-200 ${
        isDark
          ? 'bg-white/[0.03] border-white/10'
          : 'bg-white border-gray-200 shadow-sm'
      } ${isOpen ? (isDark ? 'ring-1 ring-white/10' : 'ring-1 ring-gray-200') : ''}`}
    >
      <button
        data-testid={`guide-section-${section.id}`}
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 sm:px-5 py-4 text-left transition-colors ${
          isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${section.bg}`}>
            <Icon className={`w-[18px] h-[18px] ${section.color}`} />
          </div>
          <span className="font-medium text-sm sm:text-base">{section.title}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
            isDark ? 'text-slate-400' : 'text-gray-400'
          } ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 sm:px-5 pb-4">
          <div className="space-y-2.5 pt-1">
            {section.steps.map((step, idx) => (
              <div
                key={idx}
                data-testid={`guide-step-${section.id}-${idx}`}
                className={`flex gap-3 p-3 rounded-lg ${
                  isDark ? 'bg-white/[0.03]' : 'bg-gray-50'
                }`}
              >
                <div
                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${section.bg} ${section.color}`}
                >
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm mb-0.5">{step.label}</p>
                  <p
                    className={`text-xs leading-relaxed ${
                      isDark ? 'text-slate-400' : 'text-gray-500'
                    }`}
                  >
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {section.id !== 'history' && section.id !== 'chat' && pathMap[section.id] && (
            <div
              className={`mt-4 pt-3 border-t border-dashed ${
                isDark ? 'border-white/10' : 'border-gray-200'
              }`}
            >
              <Button
                data-testid={`guide-go-to-${section.id}`}
                variant="ghost"
                size="sm"
                onClick={() => navigate(pathMap[section.id])}
                className={`text-xs ${section.color}`}
              >
                Przejdź do: {section.title}
                <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function UserGuidePage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [openSections, setOpenSections] = useState([]);

  const toggleSection = (id) => {
    setOpenSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const filteredSections = searchQuery
    ? sections.filter(
        (s) =>
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.steps.some(
            (step) =>
              step.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
              step.desc.toLowerCase().includes(searchQuery.toLowerCase())
          )
      )
    : sections;

  return (
    <div
      className={`min-h-screen ${
        isDark ? 'bg-[#0a1628] text-white' : 'bg-gray-50 text-gray-900'
      }`}
    >
      {/* Header */}
      <div
        className={`sticky top-0 z-10 border-b ${
          isDark
            ? 'bg-[#0f1d32]/95 border-white/10'
            : 'bg-white/95 border-gray-200'
        } backdrop-blur-md`}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button
            data-testid="guide-back-button"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            size="sm"
            className={
              isDark
                ? 'text-slate-300 hover:text-white'
                : 'text-gray-600 hover:text-gray-900'
            }
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex-1 text-center">
            <h1 className="text-lg sm:text-xl font-semibold flex items-center justify-center gap-2">
              <HelpCircle className="w-5 h-5 text-orange-500" />
              Instrukcja obsługi KAI
            </h1>
          </div>
          <div className="w-24" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Search */}
        <div className="mb-6">
          <div
            className={`relative rounded-lg ${
              isDark ? 'bg-white/5' : 'bg-white'
            } border ${isDark ? 'border-white/10' : 'border-gray-200'}`}
          >
            <Search
              className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                isDark ? 'text-slate-400' : 'text-gray-400'
              }`}
            />
            <input
              data-testid="guide-search-input"
              type="text"
              placeholder="Szukaj w instrukcji..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2.5 rounded-lg text-sm bg-transparent outline-none ${
                isDark
                  ? 'text-white placeholder:text-slate-500'
                  : 'text-gray-900 placeholder:text-gray-400'
              }`}
            />
          </div>
        </div>

        {/* Quick nav pills */}
        <div className="mb-8">
          <p
            className={`text-xs font-medium mb-3 ${
              isDark ? 'text-slate-400' : 'text-gray-500'
            }`}
          >
            Przejdź do sekcji
          </p>
          <div className="flex flex-wrap gap-2">
            {filteredSections.map((s) => {
              const Icon = s.icon;
              return (
                <button
                  key={s.id}
                  onClick={() => {
                    if (!openSections.includes(s.id)) toggleSection(s.id);
                    setTimeout(() => {
                      document
                        .getElementById(`section-${s.id}`)
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:scale-105 ${s.bg} ${s.color} border ${
                    isDark ? 'border-white/5' : 'border-gray-100'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {s.title}
                </button>
              );
            })}
          </div>
        </div>

        {/* No results */}
        {filteredSections.length === 0 && (
          <div
            className={`text-center py-12 ${
              isDark ? 'text-slate-400' : 'text-gray-500'
            }`}
          >
            <Search className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Brak wyników dla "{searchQuery}"</p>
          </div>
        )}

        {/* Accordion sections */}
        <div className="space-y-3">
          {filteredSections.map((section) => (
            <AccordionSection
              key={section.id}
              section={section}
              isOpen={openSections.includes(section.id)}
              onToggle={() => toggleSection(section.id)}
              isDark={isDark}
              navigate={navigate}
            />
          ))}
        </div>

        {/* Footer tip */}
        <div
          className={`mt-8 p-4 rounded-xl text-center text-xs ${
            isDark
              ? 'bg-orange-500/10 text-orange-300'
              : 'bg-orange-50 text-orange-700'
          }`}
        >
          Potrzebujesz dodatkowej pomocy? Zapytaj KAI na czacie — odpowie na
          każde pytanie dotyczące obsługi platformy.
        </div>
      </div>
    </div>
  );
}
